import { Box, Typography } from "@mui/material";
import { CSVLink } from "react-csv";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import ItemCurrentStockTable from "../../../components/Table/DocumentTable/ItemCurrentStockTable";

import InventoryService from "../../../services/Inventory";

import { getNewItemsCurrentStockReport } from "../../../features/Inventory/Report/report-actions";
import { inventoryReportActions } from "../../../features/Inventory/Report/report-slice";
import useItemCurrentStockColumnDef from "../../../hooks/Inventory/useItemCurrentStockColumnDef";

import { getAllLocation } from "../../../features/Setting/Location/location-actions";
import { formatExportQuotes } from "../../../utils/exporter";

const ItemCurrentStockView = () => {
  const { isLoading } = useSelector((state) => state.inventoryReport);
  const { allLocation } = useSelector((state) => state.location);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const [filename, setFilename] = useState("");
  const [data, setData] = useState([]);
  const csvInstance = useRef(null);
  const [showTable, setShowTable] = useState(false);

  const columnDef = useItemCurrentStockColumnDef(true, allLocation);

  const breadcrumbs = [
    {
      name: t("inventory.index"),
      to: "/inventory",
    },
    {
      name: t("inventory.report"),
      to: "/inventory/report",
    },
    {
      name: t("inventory.itemCurrentStock.index"),
    },
  ];

  const findInput = useMemo(() => {
    return {
      findManyInput: {
        include: {
          sub_level_1_list: {
            include: {
              sub_level_2_list: {
                include: {
                  sub_level_3_list: {
                    include: {
                      bin_location_list: null,
                    },
                  },
                },
              },
            },
          },
        },
      },
    };
  }, []);

  useEffect(() => {
    dispatch(getAllLocation(findInput));
  }, [dispatch, findInput]);

  const datasource = {
    getRows(params) {
      const request = params.request;
      const { filterModel } = request;

      dispatch(
        getNewItemsCurrentStockReport(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: filterModel,
            sortModel: request.sortModel,
          },
          params,
          enqueueSnackbar
        )
      );
    },
  };

  const onFilterChanged = useCallback(
    (params) => {
      params.api.onFilterChanged();
      setFilename(`${t("inventory.itemCurrentStock.index")}.csv`);
    },
    [t]
  );

  const onGridReady = (params) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  const getFiltered = async () => {
    setShowTable(true);
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  };

  const exportAllDataAsAgGridCsv = async () => {
    try {
      const filterModel = gridRef.current.api.getFilterModel();
      const sortModel = gridRef.current.columnApi
        .getColumnState()
        .filter((s) => s.sort !== null)
        .map(({ sort, colId }) => ({
          sort,
          colId,
        }));

      dispatch(inventoryReportActions.onLoading("itemCurrentStock"));
      const { results: itemCurrentStockByWarehouseReport } =
        await InventoryService.getAllNewICSReport({
          startRow: 0,
          endRow: Math.pow(10, 5),
          filterModel: filterModel,
          sortModel,
        });
      setData(formatExportQuotes(itemCurrentStockByWarehouseReport));
      dispatch(inventoryReportActions.onLoaded("itemCurrentStock"));
      setTimeout(() => {
        if (csvInstance.current) {
          csvInstance.current.link.click();
        }
      });
      enqueueSnackbar("นำออกข้อมูลสำเร็จ", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      enqueueSnackbar("นำออกข้อมูลไม่สำเร็จ", {
        variant: "error",
      });
    }
  };

  const columnDefHeader = columnDef.map((col) => ({
    label: col.headerName,
    key: col.field,
  }));

  const onFilterReset = () => {
    if (gridRef) {
      gridRef.current.api.setFilterModel({});
    }
  };

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">
          {t("inventory.itemCurrentStock.index")}
        </Typography>
        <Box>
          <CustomizedButton
            title="นำออกข้อมูล"
            variant="contained"
            onClick={exportAllDataAsAgGridCsv}
            disabled={isLoading.itemCurrentStock}
          />
          <CSVLink
            headers={columnDefHeader}
            data={data}
            filename={filename}
            ref={csvInstance}
          />
        </Box>
      </Box>
      <Box display={"flex"} my={2} gap={2} justifyContent="flex-end">
        <CustomizedButton
          title={t("button.callData")}
          variant="contained"
          onClick={getFiltered}
          disabled={isLoading.itemCurrentStock}
        />
        <CustomizedButton
          title={t("button.resetFilter")}
          variant="outlined"
          onClick={onFilterReset}
        />
      </Box>
      {showTable && (
        <ItemCurrentStockTable
          gridRef={gridRef}
          onGridReady={onGridReady}
          allLocation={allLocation}
          isReport
        />
      )}
    </>
  );
};

export default ItemCurrentStockView;
